import { useContext, useEffect } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { useLearningOpportunitiesPermissions } from '../../../../../shared/source/query-hooks/learning-opportunities';
import { useFeaturedLearningOpportunityOptions } from '../../query-hooks/featured-learning-opportunities';
import { useJobArchitectureOptionsQuery } from '../../query-hooks/job-architecture-options';
import { useJobProfilesPermissions } from '../../query-hooks/job-profiles/job-profiles';
import { useJobsPermissions } from '../../query-hooks/jobs';
import { useMyProfilePermissionsQuery } from '../../query-hooks/profile';
import { useTalentDirectoryPermissions } from '../../query-hooks/talent-directory/talent-directory';
import { useBusinessIntelligencePermissions } from '../../query-hooks/use-business-intelligence-options';
import { useUserBookmarksPermission } from '../../query-hooks/users/bookmark';
import {
    createAlignmentRecordItem,
    createAnalyticsDashboardListItem,
    createControlRoomListItem,
    createDashboardListItem,
    createJobArchitectureListItem,
    createJobProfileManagementListItem,
    createLearningManagementListItem,
    createMyGrowthListItem,
    createPositionManagementItem,
    createTalentDirectoryListItem,
} from '../navigation-menu-list-item';

import type { SideDrawerListItem } from '../navigation-menu-list-item';
import type { Just } from 'true-myth/maybe';

export const useSideDrawerLinks = (): readonly SideDrawerListItem[] => {
    const {
        applicationState: { customization, featureFlagService, isAssumedTenant, myUserProfile },
        translate,
    } = useContext(ApplicationContext);

    const { canUserManageJobs } = useJobsPermissions();
    const { canAccessTalentDirectory } = useTalentDirectoryPermissions();
    const jobArchitectureOptions = useJobArchitectureOptionsQuery();
    const { canUserFilterLearningOpportunities } = useLearningOpportunitiesPermissions();
    const { canUserGetJobProfiles } = useJobProfilesPermissions();
    const { canAccessBusinessIntelligence } = useBusinessIntelligencePermissions();
    const { data: myProfilePermissions } = useMyProfilePermissionsQuery();

    const canUseControlRoom = myProfilePermissions?.canUseControlRoom ?? false;
    const tenantId = customization.get('tenantId');
    const myUserProfileId = myUserProfile.get('id').unwrapOr('');

    const { data: featuredOptions } = useFeaturedLearningOpportunityOptions(myUserProfileId);
    const { data: permissions } = useUserBookmarksPermission(myUserProfileId);

    const showFeaturedLearningOpportunities = featuredOptions?.canReadUserFeaturedOpportunities ?? false;
    const showBookmarks = permissions?.canReadBookmarks === true;
    const showGrowthPage = showFeaturedLearningOpportunities && showBookmarks;

    const canUserGetJobArchitecture =
        jobArchitectureOptions.isSuccess && jobArchitectureOptions.data.isOk
            ? jobArchitectureOptions.data.value.canUserGetJobArchitecture
            : false;

    return [
        createDashboardListItem({ translate }),
        createMyGrowthListItem({ showGrowthPage, translate }),
        createAnalyticsDashboardListItem({ canAccessBusinessIntelligence, translate }),
        createJobProfileManagementListItem({
            canUserGetJobProfiles,
            canUserManageJobs,
            translate,
        }),
        createLearningManagementListItem({ canUserFilterLearningOpportunities, translate }),
        createTalentDirectoryListItem({ canAccessTalentDirectory, translate }),
        createJobArchitectureListItem({
            canUserGetJobArchitecture,
            featureFlagService,
            translate,
        }),
        createPositionManagementItem({ featureFlagService, translate }),
        createAlignmentRecordItem({ featureFlagService, translate }),
        createControlRoomListItem({
            canUseControlRoom,
            isAssumedTenant,
            tenantId,
            translate,
        }),
    ]
        .filter((item): item is Just<SideDrawerListItem> => item.isJust)
        .map((item) => item.value);
};

export const useEscapeKeyClose = (isOpen: boolean, onClose: () => void): void => {
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose, isOpen]);
};
